// Top-level admin Rewards page at /admin/rewards. Lists rewards
// across ALL teams (with the team name) and lets the operator create
// a reward and assign it to a team in one form. The per-team detail
// (contributions, claim, archive) still lives at
// /admin/teams/:teamId/rewards — this page links to it.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import type { TeamReward } from '../../coins/types';
import {
  listAllRewards,
  createRewardForTeam,
  ApiError,
  type CreateRewardBody,
} from './api';

interface Team {
  id: string;
  name: string;
}

export function RewardsAdminPage() {
  const qc = useQueryClient();
  const [teamId, setTeamId] = useState('');
  const rewards = useQuery({
    queryKey: ['admin-rewards', teamId],
    queryFn: () => listAllRewards(teamId || undefined),
  });
  const teams = useQuery<{ teams: Team[] }>({
    queryKey: ['teams'],
    queryFn: () => api('/api/teams'),
  });
  const createMut = useMutation({
    mutationFn: (body: CreateRewardBody) => createRewardForTeam(teamId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-rewards'] });
      qc.invalidateQueries({ queryKey: ['admin-team-rewards'] });
    },
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Rewards</h1>
        <p className="text-sm text-ink-muted">
          Real-life rewards teams can claim with coins. Create a reward and assign it to a team; the team claims it when the pool is full.
        </p>
      </div>

      <CreateForm
        teams={teams.data?.teams ?? []}
        teamId={teamId}
        onTeamChange={setTeamId}
        onSubmit={(b) => createMut.mutate(b)}
        error={createMut.error}
        isPending={createMut.isPending}
      />

      <div className="flex items-center gap-3">
        <label className="text-sm text-ink-muted">Filter by team</label>
        <select
          className="input max-w-xs"
          value={teamId}
          onChange={(e) => setTeamId(e.target.value)}
        >
          <option value="">All teams</option>
          {(teams.data?.teams ?? []).map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </div>

      {rewards.isPending ? (
        <p className="text-sm text-ink-muted">Loading…</p>
      ) : rewards.isError ? (
        <p className="text-sm text-err">Failed to load rewards.</p>
      ) : (rewards.data ?? []).length === 0 ? (
        <p className="text-sm text-ink-muted">
          No rewards yet. Use the form above to add the first one.
        </p>
      ) : (
        <ul className="space-y-3">
          {(rewards.data ?? []).map((r) => (
            <RewardRow key={r.id} reward={r} />
          ))}
        </ul>
      )}
    </div>
  );
}

function CreateForm({
  teams,
  teamId,
  onTeamChange,
  onSubmit,
  error,
  isPending,
}: {
  teams: Team[];
  teamId: string;
  onTeamChange: (id: string) => void;
  onSubmit: (b: CreateRewardBody) => void;
  error: unknown;
  isPending: boolean;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [coinCost, setCoinCost] = useState(50);
  return (
    <form
      className="card grid gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!teamId) return;
        onSubmit({
          name: name.trim(),
          description: description.trim(),
          image_url: imageUrl.trim() || undefined,
          coin_cost: coinCost,
        });
        setName('');
        setDescription('');
        setImageUrl('');
        setCoinCost(50);
      }}
    >
      <p className="text-sm font-semibold uppercase tracking-wide text-ink-muted">Add a reward</p>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="space-y-1">
          <label className="label" htmlFor="rw-team">Team</label>
          <select
            id="rw-team"
            className="input"
            required
            value={teamId}
            onChange={(e) => onTeamChange(e.target.value)}
          >
            <option value="" disabled>Select a team…</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="label" htmlFor="rw-name">Name</label>
          <input
            id="rw-name"
            className="input"
            required
            minLength={1}
            maxLength={100}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Team pizza lunch"
          />
        </div>
        <div className="space-y-1">
          <label className="label" htmlFor="rw-cost">Coin cost (pool size)</label>
          <input
            id="rw-cost"
            className="input"
            required
            type="number"
            min={1}
            max={1_000_000}
            value={coinCost}
            onChange={(e) => setCoinCost(Number(e.target.value))}
          />
        </div>
        <div className="space-y-1 md:col-span-2">
          <label className="label" htmlFor="rw-desc">Description (optional)</label>
          <textarea
            id="rw-desc"
            className="input min-h-[4rem]"
            maxLength={2000}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What the team gets, who orders it, etc."
          />
        </div>
        <div className="space-y-1 md:col-span-2">
          <label className="label" htmlFor="rw-img">Image URL (optional)</label>
          <input
            id="rw-img"
            className="input"
            type="url"
            maxLength={500}
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://…"
          />
        </div>
      </div>
      <div className="flex items-center justify-between">
        <button className="btn-primary" type="submit" disabled={isPending || !teamId}>
          {isPending ? 'Creating…' : 'Add reward'}
        </button>
        {error instanceof ApiError ? (
          <p className="text-sm text-err" role="alert">{error.message}</p>
        ) : null}
      </div>
    </form>
  );
}

function RewardRow({ reward }: { reward: TeamReward }) {
  return (
    <li className="card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold">{reward.name}</h3>
            <span className="rounded-full bg-bg-elevated px-2 py-0.5 text-xs text-ink-muted">
              {reward.team_name ?? '—'}
            </span>
            <StatusBadge status={reward.status} />
          </div>
          {reward.description ? (
            <p className="mt-1 text-sm text-ink-muted">{reward.description}</p>
          ) : null}
          <p className="mt-1 text-xs text-ink-dim">
            <span aria-hidden>🪙</span> {reward.contributed} / {reward.coin_cost} coins
            {' · '}{reward.contributor_count} contributor{reward.contributor_count === 1 ? '' : 's'}
            {reward.status === 'claimed' && reward.claimed_at ? (
              <> · claimed {new Date(reward.claimed_at).toLocaleDateString()}</>
            ) : null}
          </p>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-bg-panel">
            <div
              className="h-full bg-gradient-to-r from-accent to-accent-success"
              style={{ width: `${reward.progress_pct}%` }}
            />
          </div>
        </div>
        <Link
          to={`/admin/teams/${reward.team_id}/rewards`}
          className="text-sm text-ink-muted underline"
        >
          Manage →
        </Link>
      </div>
    </li>
  );
}

function StatusBadge({ status }: { status: TeamReward['status'] }) {
  const map: Record<TeamReward['status'], string> = {
    open: 'bg-accent-success/20 text-accent-success',
    claimed: 'bg-accent/20 text-accent',
    archived: 'bg-bg-elevated text-ink-muted',
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs ${map[status]}`}>
      {status}
    </span>
  );
}
